import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const LEGACY_TOKEN_KEY = 'longevity_token';
const LEGACY_USER_KEY = 'longevity_user';

// All API calls in this context must send the httpOnly auth cookie.
const withCreds = { withCredentials: true };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Resolve the current user from the auth cookie.
   * If a legacy localStorage token exists (pre-cookie migration), send it once
   * via Authorization header — the backend will reissue an httpOnly cookie —
   * then purge it from localStorage so the JWT is no longer XSS-reachable.
   */
  const fetchMe = async () => {
    const legacyToken = typeof window !== 'undefined'
      ? localStorage.getItem(LEGACY_TOKEN_KEY)
      : null;
    const headers = legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {};
    try {
      const response = await axios.get(`${API_BASE}/auth/me`, {
        ...withCreds,
        headers,
      });
      setUser(response.data);
    } catch (meErr) {
      // 401 on first load is expected for anonymous users; debug-log others.
      if (meErr?.response?.status && meErr.response.status !== 401) {
        console.debug('auth/me probe failed:', meErr);
      }
      setUser(null);
    } finally {
      // Purge legacy storage regardless — cookie is now the source of truth.
      if (legacyToken) {
        try {
          localStorage.removeItem(LEGACY_TOKEN_KEY);
          localStorage.removeItem(LEGACY_USER_KEY);
        } catch (lsErr) {
          console.debug('legacy token purge failed:', lsErr);
        }
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_BASE}/auth/login`, { email, password }, withCreds);
    setUser(response.data.user);
  };

  const signup = async (payload) => {
    // ref is a query-string parameter on /auth/signup so it survives the
    // request without being persisted in the JSON body schema.
    const { ref, ...body } = payload || {};
    const url = ref ? `${API_BASE}/auth/signup?ref=${encodeURIComponent(ref)}` : `${API_BASE}/auth/signup`;
    const response = await axios.post(url, body, withCreds);
    setUser(response.data.user);
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`, null, withCreds);
    } catch (e) {
      console.warn('Logout request failed (clearing local session anyway):', e);
    }
    try {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      localStorage.removeItem(LEGACY_USER_KEY);
    } catch (lsErr) {
      console.debug('legacy token purge on logout failed:', lsErr);
    }
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchMe();
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    refreshUser,
    isPro: user?.tier === 'pro',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
